import { useCallback, useEffect, useState } from 'react';
import { AxiosResponse } from 'axios';
import { ApiError } from '@/openapi';

type RemoteMethod<T, P> = ((id: string | number, payload: P) => Promise<AxiosResponse<T> | T>) | undefined;

export const useRemoteUpdate = <T, P>({
	remoteMethod,
	lazy = true // Default a true, per chiamare manualmente con fetch()
}: {
	remoteMethod: RemoteMethod<T, P>;
	lazy?: boolean;
}) => {
	const [data, setData] = useState<T | undefined>(undefined);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | undefined>(undefined);
	const [errorDetail, setErrorDetail] = useState<string | undefined>(undefined);
	const [errorCode, setErrorCode] = useState<number | undefined>(undefined);

	// Metodo per resettare l'errore
	const resetError = () => {
		setError(undefined);
		setErrorDetail(undefined);
		setErrorCode(undefined);
	};

	// Metodo per resettare lo stato
	const resetState = () => {
		setData(undefined);
		setError(undefined);
		setErrorDetail(undefined);
		setErrorCode(undefined);
	};

	const updateData = useCallback(
		async (id: string | number, payload: P) => {
			if (!remoteMethod) {
				setError('Nessun metodo remoto definito');
				return;
			}

			try {
				setLoading(true);
				const response = await remoteMethod(id, payload);
				if (response && typeof response === 'object' && 'data' in response) {
					// È un AxiosResponse
					setData(response.data);
				} else {
					// È direttamente il dato
					setData(response as T);
				}
				setError(undefined);
				setErrorDetail(undefined);
				setErrorCode(undefined);
			} catch (err: any) {
				console.error(err);
				if (err instanceof ApiError) {
					setError(err.message);
					setErrorDetail(err.body?.message);
					setErrorCode(err.status);
				} else if (err instanceof Error) {
					setError(err.message);
				} else {
					setError("Errore durante l'aggiornamento dei dati");
					if (err.body?.message) {
						setErrorDetail(err.body.message);
					}
					if (err.status) {
						setErrorCode(err.status);
					}
				}
			} finally {
				setLoading(false);
			}
		},
		[remoteMethod]
	);

	useEffect(() => {
		// Nel caso di update, probabilmente non deve eseguire automaticamente
		// anche se lazy è false, poiché è necessario conoscere l'id e i dati da aggiornare
	}, [lazy, remoteMethod]);

	return {
		data,
		loading,
		error,
		errorDetail,
		errorCode,
		fetch: updateData,
		resetError,
		resetState
	};
};
